// src/Pages/PublicUserProfile/PublicUserProfile.jsx

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Backdrop, Box, Button, CircularProgress, CssBaseline, Divider, Grid, Paper, Toolbar, Typography, Avatar, useMediaQuery,  } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';

import PlaceIcon from '@mui/icons-material/Place';
import WorkIcon from '@mui/icons-material/Work';
import CampaignIcon from '@mui/icons-material/Campaign';

import { lightTheme } from '../../../Utils/baseTheme';

// Shared site header and footer
import HomeHeader from '../../../Components/HomeHeader';
import HomeFooter from '../../../Components/HomeFooter';

// Reused global UI or hooks
import useAlertDialog from '../../../Components/useAlertDialog';
import AlertDialog from '../../../Components/AlertDialog';
import PageTitle from '../../../Components/PageTitle';
import ChatModal from './chatmodal'; // Remove if not needed

import API from '../../../API';
import defaultPfp from '../../../Components/globalAssets/ppfLogo.png';

// --------------------------------------------------------------------------------
// Styled Components (Mobile-Responsive)
// --------------------------------------------------------------------------------

const ProfileHeaderBackdrop = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  width: '100%',
  background: 'linear-gradient(45deg, #646AFF, #7B2A2A)',
  zIndex: 0,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  minHeight: 300,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    minHeight: 200,
  },
}));

const ProfileContainer = styled(Paper)(({ theme }) => ({
  maxWidth: 1200,
  width: '100%',
  margin: '-10px auto 2rem',
  padding: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: theme.shadows[10],
  position: 'relative',
  background: theme.palette.background.paper,
  [theme.breakpoints.down('md')]: {
    maxWidth: '95%',
    margin: '-10px auto 1rem',
    padding: theme.spacing(3),
    borderRadius: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '98%',
    margin: '-5px auto 1rem',
    padding: theme.spacing(2),
    borderRadius: '8px',
  },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 200,
  height: 200,
  border: `4px solid ${theme.palette.primary.main}`,
  boxShadow: theme.shadows[6],
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    width: 150,
    height: 150,
    border: `3px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down('sm')]: {
    width: 120,
    height: 120,
    border: `2px solid ${theme.palette.primary.main}`,
    marginBottom: theme.spacing(1),
  },
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
  color: theme.palette.common.white,
  transition: 'transform 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    borderRadius: '8px',
    '&:hover': {
      transform: 'none', // Disable hover effect on mobile
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #646AFF, #7B2A2A)',
  color: '#FFF',
  borderRadius: '25px',
  padding: '12px 32px',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[4],
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 24px',
    fontSize: '0.875rem',
    letterSpacing: '0.5px',
    '&:hover': {
      transform: 'none', // Disable hover effect on mobile
    },
  },
}));

/**
 * A small utility component to display an info label/value pair with an icon.
 */
const InfoItem = ({ label, value, icon }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Paper
        sx={{
          p: isMobile ? 1.5 : 2,
          height: '100%',
          borderRadius: isMobile ? '8px' : '12px',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: isMobile ? 'none' : 'translateY(-4px)',
            boxShadow: isMobile ? 1 : 3,
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
          <Box
            sx={{
              background: theme.palette.primary.main,
              borderRadius: '50%',
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: isMobile ? '1rem' : '1.5rem',
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography 
              variant={isMobile ? 'caption' : 'subtitle2'} 
              color="textSecondary" 
              gutterBottom
              sx={{ 
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}
            >
              {label}
            </Typography>
            <Typography 
              variant={isMobile ? 'body2' : 'h6'} 
              fontWeight="600"
              sx={{ 
                wordBreak: 'break-word',
                lineHeight: 1.3,
              }}
            >
              {value || 'N/A'}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Grid>
  );
};

/**
 * Card to display total projects/campaigns count
 */
const TotalProjectsCard = ({ count }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Grid item xs={12} sm={6} md={4}>
      <StatsCard>
        <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
          <CampaignIcon sx={{ fontSize: isMobile ? 32 : 40 }} />
          <Box>
            <Typography variant={isMobile ? 'caption' : 'subtitle2'}>
              Total Projects
            </Typography>
            <Typography variant={isMobile ? 'h5' : 'h3'}>
              {count}
            </Typography>
          </Box>
        </Box>
      </StatsCard>
    </Grid>
  );
};

// --------------------------------------------------------------------------------
// Main PublicUserProfile Component
// --------------------------------------------------------------------------------

export default function PublicUserProfile() {
  const { userId } = useParams();
  const { dialogState, openDialog } = useAlertDialog();
  const [chatOpen, setChatOpen] = useState(false);
  const [targetUserId, setTargetUserId] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch user data publicly using react-query
  const {
    data: user,
    isLoading,
    error,
  } = useQuery(['userPublicDetails', userId], () => API.users.fetchPublicDetails(userId), {
    enabled: !!userId,
    refetchOnWindowFocus: false,
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      openDialog('Error', `Failed to load user data: ${error.message || 'Unknown error'}`);
    }
  }, [error, openDialog]);

  // Show loading
  if (isLoading) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // If no user returned
  if (!user) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Toolbar />
        <Box sx={{ p: isMobile ? 2 : 4 }}>
          <Typography variant="h6">No user data found for &quot;{userId}&quot;.</Typography>
        </Box>
        <HomeFooter />
      </ThemeProvider>
    );
  }

  // Destructure user data
  const { first_name, last_name, company, campaigns = [], created_at, pfphref } = user;

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      {/* Title that displays in the browser tab */}
      <PageTitle title={`Public Profile - @${userId}`} />
      {/* Global alert dialog hook */}
      <AlertDialog alertState={dialogState} />

      {/* Site Header (Nav) */}
      <HomeHeader />
      <Toolbar />

      {/* Content with gradient backdrop for the "hero" area */}
      <ProfileHeaderBackdrop>
        <ProfileContainer>
          <Box display="flex" flexDirection="column" alignItems="center" mb={isMobile ? 2 : 4}>
            {/* User Avatar */}
            <UserAvatar src={user.phphref || defaultPfp} alt={`${first_name ?? ''} ${last_name ?? ''}`} />

            {/* Name */}
            <Typography 
              variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h2'} 
              gutterBottom 
              sx={{ 
                fontWeight: 700, 
                letterSpacing: isMobile ? 0.5 : 1,
                textAlign: 'center',
                wordBreak: 'break-word',
                px: 1,
              }}
            >
              {first_name} {last_name}
            </Typography>

            {/* Location / Industry */}
            <Box 
              display="flex" 
              flexDirection={isMobile ? 'column' : 'row'}
              justifyContent="center" 
              alignItems="center"
              gap={isMobile ? 1 : 2} 
              mb={2}
              sx={{ width: '100%', px: 1 }}
            >
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  textAlign: 'center',
                  wordBreak: 'break-word',
                }}
              >
                <PlaceIcon fontSize={isMobile ? 'small' : 'inherit'} sx={{ mr: 1 }} />
                {company?.location || 'Global'}
              </Typography>
              {company?.industry && (
                <Typography 
                  variant={isMobile ? 'body1' : 'h6'} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    textAlign: 'center',
                    wordBreak: 'break-word',
                  }}
                >
                  <WorkIcon fontSize={isMobile ? 'small' : 'inherit'} sx={{ mr: 1 }} />
                  {company.industry}
                </Typography>
              )}
            </Box>

            {/* Example: Chat button (remove if you don't need public chat) */}
            <ActionButton
              onClick={() => {
                setTargetUserId(userId);
                setChatOpen(true);
              }}
            >
               Login
            </ActionButton>
          </Box>

          <Divider sx={{ 
            my: isMobile ? 2 : 4, 
            borderWidth: 2, 
            borderColor: 'divider' 
          }} />

          {/* Stats / Info row */}
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 2 : 4 }}>
            <TotalProjectsCard count={campaigns.length} />
            <InfoItem label="Company" value={company?.name} icon={<WorkIcon />} />
            <InfoItem
              label="Member Since"
              value={created_at ? new Date(created_at).toLocaleDateString() : 'N/A'}
              icon={<WorkIcon />}
            />
          </Grid>

          <Divider sx={{ 
            my: isMobile ? 2 : 4, 
            borderWidth: 2, 
            borderColor: 'divider' 
          }} />

          {/* Active Campaigns */}
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              mb: isMobile ? 2 : 3,
              textAlign: isMobile ? 'center' : 'left',
            }}
          >
            Active Campaigns
          </Typography>
          {campaigns.length > 0 ? (
            <Grid container spacing={isMobile ? 2 : 3}>
              {campaigns.map((campaign) => (
                <Grid item xs={12} sm={6} md={4} key={campaign.id}>
                  <Paper
                    sx={{
                      p: isMobile ? 2 : 3,
                      height: '100%',
                      borderRadius: isMobile ? '8px' : '16px',
                      transition: 'transform 0.3s ease',
                      '&:hover': {
                        transform: isMobile ? 'none' : 'translateY(-5px)',
                        boxShadow: isMobile ? 2 : 4,
                      },
                    }}
                  >
                    <Typography 
                      variant={isMobile ? 'subtitle1' : 'h6'} 
                      gutterBottom 
                      sx={{ 
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                      }}
                    >
                      {campaign.name}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      paragraph
                      sx={{ 
                        wordBreak: 'break-word',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: isMobile ? 3 : 4,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {campaign.description}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography 
              variant="body1" 
              color="textSecondary"
              sx={{ 
                textAlign: 'center',
                py: isMobile ? 2 : 4,
              }}
            >
              No active campaigns currently running.
            </Typography>
          )}
        </ProfileContainer>
      </ProfileHeaderBackdrop>

      {/* Chat Modal (Remove if not needed) */}
      <ChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        recipientId={targetUserId}
      />

      {/* Site Footer */}
      <HomeFooter />
    </ThemeProvider>
  );
}